"use client";

import { useCallback, useEffect, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { ChevronRight } from "lucide-react";
import { resolveItemUrl } from "@/lib/directus";
import type { SafeNavigationItem, SocialLinkType } from "@/lib/directus/typeGuards";
import type { ButtonDataType } from "@/shared/types";
import { LinkButton, NavMenuDot, toLinkArrowVariant } from "@/shared/components";
import { SocialIcon } from "@/shared/components/SocialIcon";
import { getHeaderSocialLinks } from "@/shared/utils/headerSocialLinks";
import { lockPageScroll, unlockPageScroll } from "@/lib/scrollLock";
import { cn } from "@/lib/utils";
import menuDuoIcon from "public/icons/menu-duo.svg";
import menuCloseIcon from "public/icons/menu-close.svg";

interface NavLevel {
  title: string | null;
  items: SafeNavigationItem[];
}

export interface HamburgerMenuProps {
  navigation?: SafeNavigationItem[] | null;
  primaryCta?: ButtonDataType;
  phone?: string | null;
  phoneSecondary?: string | null;
  email?: string | null;
  address?: string | null;
  socialLinks?: SocialLinkType[] | null;
  searchOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  onNavLevelChange?: (state: { canGoBack: boolean; goBack: () => void }) => void;
}

export function HamburgerMenu({
  navigation,
  primaryCta,
  phone,
  phoneSecondary,
  email,
  address,
  socialLinks,
  searchOpen = false,
  onOpenChange,
  onNavLevelChange,
}: HamburgerMenuProps) {
  const [isOpen, setOpen] = useState(false);
  const [navStack, setNavStack] = useState<NavLevel[]>([
    { title: null, items: navigation ?? [] },
  ]);
  const [prevNavigation, setPrevNavigation] = useState(navigation);
  const [prevSearchOpen, setPrevSearchOpen] = useState(searchOpen);

  if (navigation !== prevNavigation) {
    setPrevNavigation(navigation);
    setNavStack([{ title: null, items: navigation ?? [] }]);
  }

  if (searchOpen !== prevSearchOpen) {
    setPrevSearchOpen(searchOpen);
    if (searchOpen && isOpen) {
      setOpen(false);
      setNavStack([{ title: null, items: navigation ?? [] }]);
    }
  }

  const currentLevel = navStack[navStack.length - 1];
  const isRoot = navStack.length === 1;
  const headerSocialLinks = getHeaderSocialLinks(socialLinks);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    lockPageScroll();

    return () => {
      unlockPageScroll();
    };
  }, [isOpen]);

  useEffect(() => {
    onOpenChange?.(isOpen);
  }, [isOpen, onOpenChange]);

  function drillUp() {
    setNavStack((prev) => (prev.length > 1 ? prev.slice(0, -1) : prev));
  }

  const handleDrillUp = useCallback(() => {
    drillUp();
  }, []);

  useEffect(() => {
    onNavLevelChange?.({
      canGoBack: !isRoot,
      goBack: handleDrillUp,
    });
  }, [isRoot, onNavLevelChange, handleDrillUp]);

  function drillDown(item: SafeNavigationItem) {
    const children = item.children ?? [];
    setNavStack((prev) => [...prev, { title: item.title, items: children }]);
  }

  function handleClose() {
    setOpen(false);
    setNavStack([{ title: null, items: navigation ?? [] }]);
  }

  function handleToggle() {
    if (isOpen) {
      handleClose();
      return;
    }
    setNavStack([{ title: null, items: navigation ?? [] }]);
    setOpen(true);
  }

  const hasContacts = Boolean(phone || phoneSecondary || email || address);
  const hasSocialLinks = headerSocialLinks.length > 0;

  const rootItemClass = "p-4 md:px-6 md:py-4";
  const subItemClass = "px-4 py-2 md:px-6";

  return (
    <div className="flex shrink-0 items-center justify-end desktop:hidden">
      <button
        type="button"
        onClick={handleToggle}
        aria-expanded={isOpen}
        aria-label={isOpen ? "Закрыть меню" : "Открыть меню"}
        className="flex size-10 items-center justify-center text-foreground transition-colors hover:text-primary"
      >
        <Image
          src={isOpen ? menuCloseIcon : menuDuoIcon}
          alt=""
          width={isOpen ? 32 : 40}
          height={isOpen ? 32 : 40}
          className={isOpen ? "size-8" : "size-10"}
        />
      </button>

      {isOpen && (
        <div
          className="fixed inset-x-0 bottom-0 top-[var(--header-height)] z-40 flex flex-col overflow-hidden bg-background"
          role="dialog"
          aria-modal="true"
          aria-label="Меню сайта"
        >
          <nav
            className={cn(
              "min-h-0 flex-1 overflow-y-auto",
              !isRoot && "scrollbar-thin-visible"
            )}
          >
            <div
              className={cn(
                "flex flex-col py-6",
                !isRoot && "gap-4"
              )}
            >
              {currentLevel.items.map((item) => {
                const hasChildren = (item.children ?? []).length > 0;
                const parentUrl =
                  item.type !== "group" ? resolveItemUrl(item) : null;
                const hasParentLink = parentUrl !== null && parentUrl !== "#";

                if (hasChildren) {
                  return (
                    <div
                      key={item.id}
                      className={cn(
                        "flex w-full items-center justify-between gap-4 font-sans text-base font-normal leading-normal text-secondary",
                        isRoot ? rootItemClass : subItemClass
                      )}
                    >
                      {hasParentLink ? (
                        <Link
                          href={parentUrl}
                          onClick={handleClose}
                          className={cn(
                            "flex min-w-0 flex-1 items-center gap-2 text-left transition-colors hover:text-primary",
                            isRoot && "uppercase"
                          )}
                        >
                          {isRoot && <NavMenuDot />}
                          {item.title}
                        </Link>
                      ) : (
                        <button
                          type="button"
                          className={cn(
                            "flex min-w-0 flex-1 items-center gap-2 text-left transition-colors hover:text-primary",
                            isRoot && "uppercase"
                          )}
                          onClick={() => drillDown(item)}
                        >
                          {isRoot && <NavMenuDot />}
                          {item.title}
                        </button>
                      )}
                      <button
                        type="button"
                        className="flex shrink-0 items-center justify-center transition-colors hover:text-primary"
                        onClick={() => drillDown(item)}
                        aria-label={`Открыть подменю «${item.title ?? ""}»`}
                      >
                        <ChevronRight className="size-6" aria-hidden />
                      </button>
                    </div>
                  );
                }

                const url = resolveItemUrl(item);
                const domain = process.env.NEXT_PUBLIC_DOMAIN || "";
                const targetIsBlank =
                  (url.startsWith("http://") || url.startsWith("https://")) &&
                  (!domain || !url.includes(domain));

                return (
                  <Link
                    key={item.id}
                    href={url}
                    target={targetIsBlank ? "_blank" : undefined}
                    rel={targetIsBlank ? "noopener noreferrer" : undefined}
                    onClick={handleClose}
                    className={cn(
                      "flex items-center gap-2 font-sans text-base font-normal leading-normal text-secondary transition-colors hover:text-primary",
                      isRoot ? rootItemClass : subItemClass,
                      isRoot && "uppercase"
                    )}
                  >
                    {isRoot && <NavMenuDot />}
                    {item.title}
                  </Link>
                );
              })}
            </div>
          </nav>

          {primaryCta && (
            <div className="shrink-0 border-b border-t border-border bg-background">
              <LinkButton
                href={primaryCta.href || "#"}
                variant={toLinkArrowVariant(primaryCta.variant)}
                size={primaryCta.size}
                onClick={handleClose}
                className="px-4 py-6 md:px-6 md:py-4"
              >
                {primaryCta.children}
              </LinkButton>
            </div>
          )}

          {hasContacts && (
            <div className="shrink-0 bg-muted px-4 py-6 md:px-6 md:pb-12 md:pt-10">
              <div className="flex flex-col gap-4 font-sans text-base font-normal leading-[1.2] text-secondary">
                {(phone || phoneSecondary) && (
                  <div className="flex flex-col gap-4">
                    {phone && (
                      <a
                        href={`tel:${phone.replace(/\s/g, "")}`}
                        className="whitespace-nowrap font-bold hover:underline"
                      >
                        {phone}
                      </a>
                    )}
                    {phoneSecondary && (
                      <a
                        href={`tel:${phoneSecondary.replace(/\s/g, "")}`}
                        className="whitespace-nowrap font-bold hover:underline"
                      >
                        {phoneSecondary}
                      </a>
                    )}
                  </div>
                )}
                {email && (
                  <a
                    href={`mailto:${email}`}
                    className="font-normal underline hover:text-primary"
                  >
                    {email}
                  </a>
                )}
                {address && <p className="font-normal">{address}</p>}

                {hasSocialLinks && (
                  <div className="flex items-center gap-6">
                    {headerSocialLinks.map((social) => (
                      <Link
                        key={social.service}
                        href={social.url || "#"}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex size-10 items-center justify-center md:size-8"
                        aria-label={social.service}
                      >
                        <SocialIcon
                          service={social.service}
                          variant="menu"
                          className="size-10 md:size-8"
                        />
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
